import type { ContractDocument, ContractWithVersion, TemplateVariableView } from "@closer/shared";
import { Button, Card, FormField, Input, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * ŞABLONDAN SÖZLEŞME AKIŞI (CLOS-131 `[114]` · CLOS-132 `[113]`).
 *
 * ### ⚠ Önce METİN, sonra sözleşme
 *
 * Kart: *"oluşturulan metin KULLANICI ONAYINA sunulur."* Ekran önce doldurulmuş metni
 * gösteriyor; sözleşme ancak kullanıcı onayladıktan sonra doğuyor. Doğrudan yaratsaydık,
 * hukukçu imzalayacağı metni ilk kez kayıt oluştuktan SONRA görürdü.
 *
 * ### ⚠ Eksik değişkenin hatası ALANIN yanında
 *
 * Sunucu hangi değişkenin eksik olduğunu alan hatası olarak söylüyor; ekran onu ilgili
 * kutucuğa yazıyor. Tek bir genel "bir şeyler eksik" mesajı, on değişkenli bir şablonda
 * kullanıcıyı arama yapmaya zorlardı.
 */

export function TemplateContractPage() {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [baslik, setBaslik] = useState("");
  const [degerler, setDegerler] = useState<Record<string, string>>({});
  const [onizleme, setOnizleme] = useState<ContractDocument | null>(null);
  const [alanHatalari, setAlanHatalari] = useState<Record<string, string>>({});

  const degiskenler = useQuery({
    queryKey: tenantKey(tenantId, "template", id, "degiskenler"),
    queryFn: async () =>
      (await api<TemplateVariableView[]>(`/api/templates/${id}/variables`, { tenantId })).data,
  });

  const kullaniciAlanlari = (degiskenler.data ?? []).filter((d) => !d.filledBySystem);

  function hatalariAl(hata: unknown) {
    if (hata instanceof ApiRequestError && hata.fields) {
      const yeni: Record<string, string> = {};
      for (const alan of hata.fields) {
        yeni[alan.field.replace(/^variables\./, "")] = alan.message;
      }
      setAlanHatalari(yeni);
      return true;
    }
    return false;
  }

  const onizle = useMutation({
    mutationFn: async () =>
      (
        await api<{ document: ContractDocument }>(`/api/templates/${id}/preview`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({ title: baslik.trim(), variables: degerler }),
        })
      ).data,
    onSuccess: (sonuc) => {
      setAlanHatalari({});
      setOnizleme(sonuc.document);
    },
    onError: (hata) => {
      if (hatalariAl(hata)) return;
      toast.error(hata instanceof ApiRequestError ? hata.message : "Ön izleme alınamadı");
    },
  });

  const olustur = useMutation({
    mutationFn: async () =>
      (
        await api<ContractWithVersion>(`/api/contracts/from-template/${id}`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({ title: baslik.trim(), variables: degerler }),
        })
      ).data,
    onSuccess: (sonuc) => {
      toast.success("Sözleşme oluşturuldu");
      void navigate(`/app/sozlesmeler/${sonuc.contract.id}`);
    },
    onError: (hata) => {
      if (hatalariAl(hata)) return;
      toast.error(hata instanceof ApiRequestError ? hata.message : "Sözleşme oluşturulamadı");
    },
  });

  if (degiskenler.isPending) {
    return (
      <div role="status" aria-label="Şablon yükleniyor">
        <Skeleton lines={5} />
      </div>
    );
  }

  if (degiskenler.error) {
    return (
      <StateView
        state={resolveErrorState(degiskenler.error, { idempotent: true })}
        onRetry={() => void degiskenler.refetch()}
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-5">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="mr-auto text-2xl font-bold text-fg-heading sm:text-3xl">
          Şablondan sözleşme
        </h1>
        {/* ⚠ İPTAL: hiçbir kayıt oluşmadığı için geri dönmek yeterli. */}
        <Link className="text-sm font-medium text-primary hover:underline" to="/app/sozlesmeler">
          Vazgeç
        </Link>
      </header>

      <Card title="Bilgiler">
        <div className="flex flex-col gap-3">
          <FormField label="Sözleşme başlığı" required>
            <Input
              value={baslik}
              onChange={(e) => setBaslik(e.target.value)}
              placeholder="Örn. Müvekkil A — NDA"
            />
          </FormField>

          {kullaniciAlanlari.map((degisken) => (
            <FormField
              key={degisken.name}
              label={degisken.label}
              error={alanHatalari[degisken.name]}
              required
            >
              <Input
                value={degerler[degisken.name] ?? ""}
                onChange={(e) => setDegerler((ö) => ({ ...ö, [degisken.name]: e.target.value }))}
              />
            </FormField>
          ))}

          <div className="flex flex-wrap items-center justify-end gap-2">
            {/*
              ⚠ SEBEP YAZILI: "Sözleşmeyi oluştur" ön izleme görülmeden pasif kalıyor ve
              düğmenin kendisi bunu söylemiyordu. Başlığı yazan kullanıcı, pasif bir düğmeye
              bakıp neyi eksik bıraktığını bilmiyordu — kapının kendisi doğru, görünmez
              olması yanlıştı.
            */}
            {onizleme === null && baslik.trim().length > 0 ? (
              <p className="mr-auto text-sm text-fg-muted">
                Sözleşmeyi oluşturmak için önce “Metni gör” ile ön izlemeyi açın.
              </p>
            ) : null}
            <Button
              type="button"
              variant="secondary"
              loading={onizle.isPending}
              disabled={baslik.trim().length === 0}
              onClick={() => onizle.mutate()}
            >
              Metni gör
            </Button>
            {/* ⚠ Oluşturma ancak ÖN İZLEME görüldükten sonra: onaya sunulan metin bu. */}
            <Button
              type="button"
              loading={olustur.isPending}
              disabled={onizleme === null}
              onClick={() => olustur.mutate()}
            >
              Sözleşmeyi oluştur
            </Button>
          </div>
        </div>
      </Card>

      {onizleme && (
        <Card title="Oluşturulacak metin">
          <div className="flex flex-col gap-4">
            {onizleme.clauses.map((madde) => (
              <section key={madde.id} className="flex flex-col gap-1">
                {madde.title && (
                  <h2 className="text-base font-semibold text-fg-heading">{madde.title}</h2>
                )}
                {madde.blocks.map((blok, i) => (
                  <p key={i} className="text-sm text-fg">
                    {blok.text}
                  </p>
                ))}
              </section>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
